import React, { Component } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { API_ROOT } from '../../../api/constants'; // Used to fetch user registration link

import * as authActions from '../../auth/auth.actions'; // Used in user control to 'log in as user' by admin
import * as userActions from '../user.actions';
import * as teamActions from '../../team/team.actions';
import * as channelActions from '../../channel/channel.actions';
import * as regionActions from '../../region/region.actions';
import { UserRoles } from '../user.constants';
import { getAllUsersGroupedByChannel, getCurrentUser } from '../user.selectors';

import { ContentBlock } from '../../pages/components';
import Accordion, { AccordionHeader } from '../../common/components/Accordion';
import Table from '../../common/components/Table';
import Button from '../../../ui/button/Button';
import { ICONS as icons } from '../../../icons/Icons';
import Icon from '../../../icons/Icon';
import { partial } from '../../common/utils/partial';
import { formatDate } from '../../../utils/date-utils';
import colors from '../../../utils/colors';
import { Localize } from '../../../locale/localization';
import Dialog from '../../common/components/Dialog';
import UserControl from './UserControl';

import UserSearchForm from './UserSearchForm';

class UserListContainer extends Component {
  state = {
    dialogOpen: false, // Used in channel, region or team delete
    deletableEntity: null, // Used in channel, region or team delete
    userControllerDialog: false, // Used in user control dialog
    userControllerEntity: null, // Used in user control dialog
  };

  componentWillMount = () => {
    const {
      currentUser,
      getUsers,
      getChannel,
      getChannels,
      getRegions,
      getRegionsByChannel,
      getTeam,
      getTeams,
    } = this.props;

    getUsers();
    if (currentUser.role === UserRoles.SUPERUSER) {
      getChannels();
      getRegions();
      getTeams();
    } else if (currentUser.role === UserRoles.SALESMANAGER) {
      getChannel(currentUser.channel._id);
      getRegionsByChannel(currentUser.channel._id);
      getTeams();
    } else if (currentUser.role === UserRoles.REGIONMANAGER) {
      getChannel(currentUser.channel._id);
      getTeams();
    } else {
      getChannel(currentUser.channel._id);
      getTeam(currentUser.team._id);
    }
  };

  onDeleteEntity = (id, entity) => {
    this.setState({
      dialogOpen: true,
      deletableEntity: {
        id,
        entity,
      },
    });
  };

  cancelDelete = event => {
    this.setState({
      dialogOpen: false,
      deletableEntity: null,
    });
  };

  confirmDelete = event => {
    const { deleteUser, deleteTeam, deleteRegion, deleteChannel } = this.props;

    const onDelete = () => {
      this.setState({
        dialogOpen: false,
        deletableEntity: null,
      });
    };

    const { deletableEntity } = this.state;
    const { entity, id } = deletableEntity;
    switch (entity) {
      case 'user':
        deleteUser(id).then(onDelete);
        break;
      case 'team':
        deleteTeam(id).then(onDelete);
        break;
      case 'region':
        deleteRegion(id).then(onDelete);
        break;
      case 'channel':
        deleteChannel(id).then(onDelete);
        break;
      default:
    }
  };

  onUserControl = (id, entity) => {
    const registrationLink = `${API_ROOT}register/${entity.emailToken}`;
    this.setState({
      userControllerDialog: true,
      userControllerEntity: {
        id,
        entity,
        registrationLink,
      },
    });
  };

  cancelUserControl = event => {
    this.setState({
      userControllerDialog: false,
      userControllerEntity: null,
    });
  };

  renderBlockHeaderActions = () => {
    return (
      <NavLink to="/admin/users/add">
        <Button theme="light" icon="#plus">
          Kutsu käyttäjiä
        </Button>
      </NavLink>
    );
  };

  renderChannelHeader = (name, id, canRemoveChannel) => {
    return (
      <AccordionHeader>
        <Icon className="open" icon={icons.MINUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <Icon className="closed" icon={icons.PLUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <h2>
          <span className="title">{name}</span>
          <div className="action-wrap">
            {canRemoveChannel ? (
              <a onClick={partial(this.onDeleteEntity, id, 'channel')}>Poista</a>
            ) : (
              <span>Poista</span>
            )}
          </div>
        </h2>
      </AccordionHeader>
    );
  };

  renderRegionHeader = (name, id, canRemoveRegion) => {
    return (
      <AccordionHeader>
        <Icon className="open" icon={icons.MINUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <Icon className="closed" icon={icons.PLUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <h3>
          <span className="title">{name}</span>
          {id !== 'orphan' && (
            <div className="action-wrap">
              {canRemoveRegion ? (
                <a onClick={partial(this.onDeleteEntity, id, 'region')}>Poista</a>
              ) : (
                <span>Poista</span>
              )}
            </div>
          )}
        </h3>
      </AccordionHeader>
    );
  };

  renderTeamHeader = (name, id, canRemoveTeam) => {
    return (
      <AccordionHeader>
        <Icon className="open" icon={icons.MINUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <Icon className="closed" icon={icons.PLUS} width="1.25rem" height="1.25rem" stroke={colors.BRAND_RED} />
        <h4>
          <span className="title">{name}</span>
          <div className="action-wrap">
            {canRemoveTeam ? <a onClick={partial(this.onDeleteEntity, id, 'team')}>Poista</a> : <span>Poista</span>}
          </div>
        </h4>
      </AccordionHeader>
    );
  };

  renderUserHeader = () => {
    return (
      <tr>
        <th>Käyttäjä</th>
        <th>Liittynyt</th>
        <th>Oikeudet</th>
        <th />
      </tr>
    );
  };

  renderUserRow = (adminUserId, canRemoveAdminUser, user) => {
    const { _id, firstName, lastName, email, createdAt, role } = user;
    const { currentUser } = this.props;

    const displayName = firstName && lastName ? `${firstName} ${lastName}` : email;
    const isAdminUser = adminUserId === _id;
    const allowedAdmins = ["salesmanager","regionmanager","teamlead"];
    return (
      <tr key={_id}>
        <td>{displayName}</td>
        <td>{formatDate(createdAt)}</td>
        <td>{Localize(`user.role.${role}`)}</td>
        <td>
          <div className="action-wrap">
            {!isAdminUser || canRemoveAdminUser ? (
              <div>
                {currentUser.role === 'superuser' && <a onClick={partial(this.onUserControl, _id, user)}>Hallinta</a>}
                {(currentUser.role !== 'superuser' && allowedAdmins.indexOf(currentUser.role) > -1) && <a onClick={partial(this.onDeleteEntity, _id, 'user')}>Poista</a>}
              </div>
            ) : (
              <span>{currentUser.role === 'superuser' ? 'Hallinta' : 'Poista'}</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  renderWithChannels = () => {
    const { users } = this.props;

    return [<UserSearchForm key="_searchForm" initialCount={5} />].concat(
      users.map(channel => (
        <Accordion
          key={channel._id}
          renderHeaderContent={partial(
            this.renderChannelHeader,
            channel.name,
            channel._id,
            channel.regions.length === 0 && !channel.adminUser
          )}
          className="channel"
        >
          {channel.adminUser &&
            channel.adminUser._id && (
              <Table
                /*items={[channel.adminUser]}*/
                items={channel.members.filter(m => m.role === 'salesmanager')}
                renderHeader={this.renderUserHeader}
                renderItem={partial(this.renderUserRow, channel.adminUser._id, channel.regions.length === 0)}
                widths={['30%', '25%', '25%', '20%']}
              />
            )}
          {channel.regions.map((region, index) => (
            <Accordion
              key={region._id || index}
              className="region"
              renderHeaderContent={partial(
                this.renderRegionHeader,
                region.name,
                region._id,
                region.teams.length === 0 && !region.adminUser
              )}
            >
              {region.adminUser &&
                region.adminUser._id && (
                  <Table
                    /*items={[region.adminUser]}*/
                    items={channel.members.filter(m => m.role === 'regionmanager' && m.region === region._id)}
                    renderHeader={this.renderUserHeader}
                    renderItem={partial(this.renderUserRow, region.adminUser._id, region.teams.length === 0)}
                    widths={['30%', '25%', '25%', '20%']}
                  />
                )}
              {region.teams.map(team => (
                <Accordion
                  key={team._id}
                  className="team"
                  toggleDisabled={team.members.length === 0}
                  renderHeaderContent={partial(this.renderTeamHeader, team.name, team._id, team.members.length === 0)}
                >
                  {team.members &&
                    team.adminUser && (
                      <Table
                        items={team.members}
                        renderHeader={this.renderUserHeader}
                        renderItem={partial(this.renderUserRow, team.adminUser._id, team.members.length <= 1)}
                        widths={['30%', '25%', '25%', '20%']}
                      />
                    )}
                </Accordion>
              ))}
            </Accordion>
          ))}
        </Accordion>
      ))
    );
  };

  renderWithRegions = () => {
    const { users } = this.props;

    return users.map(channel =>
      channel.regions.map((region, index) => (
        <Accordion
          key={region._id || index}
          className="region"
          renderHeaderContent={partial(
            this.renderRegionHeader,
            region.name,
            region._id,
            region.teams.length === 0 && !region.adminUser
          )}
        >
          {region.adminUser &&
            region.adminUser._id && (
              <Table
                /*items={[region.adminUser]}*/
                items={channel.members.filter(m => m.role === 'regionmanager' && m.region === region._id)}
                renderHeader={this.renderUserHeader}
                renderItem={partial(this.renderUserRow, region.adminUser._id, region.teams.length === 0)}
                widths={['30%', '25%', '25%', '20%']}
              />
            )}
          {region.teams.map(team => (
            <Accordion
              key={team._id}
              className="team"
              toggleDisabled={team.members.length === 0}
              renderHeaderContent={partial(this.renderTeamHeader, team.name, team._id, team.members.length === 0)}
            >
              {team.members &&
                team.adminUser && (
                  <Table
                    items={team.members}
                    renderHeader={this.renderUserHeader}
                    renderItem={partial(this.renderUserRow, team.adminUser._id, team.members.length <= 1)}
                    widths={['30%', '25%', '25%', '20%']}
                  />
                )}
            </Accordion>
          ))}
        </Accordion>
      ))
    );
  };

  renderWithTeams = () => {
    const { users } = this.props;

    return users.map(channel =>
      channel.regions.map(region =>
        region.teams.map(team => (
          <Accordion
            key={team._id}
            className="team"
            toggleDisabled={team.members.length === 0}
            renderHeaderContent={partial(this.renderTeamHeader, team.name, team._id, team.members.length === 0)}
          >
            {team.members &&
              team.adminUser && (
                <Table
                  items={team.members}
                  renderHeader={this.renderUserHeader}
                  renderItem={partial(this.renderUserRow, team.adminUser._id, team.members.length <= 1)}
                  widths={['30%', '25%', '25%', '20%']}
                />
              )}
          </Accordion>
        ))
      )
    );
  };

  renderWithSingleTeam = () => {
    const { users } = this.props;

    return users.map(channel =>
      channel.regions.map(region =>
        region.teams.map(
          team =>
            team.members &&
            team.adminUser && (
              <Table
                items={team.members}
                renderHeader={this.renderUserHeader}
                renderItem={partial(this.renderUserRow, team.adminUser._id, team.members.length <= 1)}
                widths={['30%', '25%', '25%', '20%']}
              />
            )
        )
      )
    );
  };

  renderRoleContent(role) {
    switch (role) {
      case UserRoles.SUPERUSER:
        return this.renderWithChannels();
      case UserRoles.SALESMANAGER:
        return this.renderWithRegions();
      case UserRoles.REGIONMANAGER:
        return this.renderWithTeams();
      case UserRoles.TEAMLEADER:
        return this.renderWithSingleTeam();
      default:
        return null;
    }
  }

  render() {
    const {
      users,
      currentUser,
      updateUser,
      updateTeam,
      updateRegion,
      updateChannel,
      deleteUser,
      deleteTeam,
      deleteRegion,
      deleteChannel,
      adminLoginAsUser,
    } = this.props;

    if (!users) return null;
    const { deletableEntity } = this.state;
    const { getTeams } = this.props;
    const dialogTitle = !deletableEntity ? null : Localize(`form.delete-${deletableEntity.entity}.confirm`);
    const dialogConfirmLabel = !deletableEntity ? null : Localize(`admin.delete.${deletableEntity.entity}`);

    let title = `${Localize('admin.users')}`;
    switch (currentUser.role) {
      case UserRoles.SUPERUSER:
        break;
      case UserRoles.SALESMANAGER:
        if (currentUser.channel && currentUser.channel.name) {
          title = `${title}: ${currentUser.channel.name}`;
        }
        break;
      case UserRoles.REGIONMANAGER:
        if (currentUser.region && currentUser.region.name) {
          title = `${title}: ${currentUser.region.name}`;
        }
        break;
      case UserRoles.TEAMLEADER:
        if (currentUser.team && currentUser.team.name) {
          title = `${title}: ${currentUser.team.name}`;
        }
        break;
      default:
    }

    return (
      <ContentBlock title={title} renderActions={this.renderBlockHeaderActions}>
        {this.renderRoleContent(currentUser.role)}
        <Dialog
          open={this.state.dialogOpen}
          title={dialogTitle}
          cancelLabel={Localize('action.cancel')}
          confirmLabel={dialogConfirmLabel}
          onCancel={this.cancelDelete}
          onConfirm={this.confirmDelete}
        >
          <p>{Localize('form.no.cancel')}</p>
        </Dialog>
        <UserControl
          open={this.state.userControllerDialog}
          onCancel={this.cancelUserControl}
          onConfirm={this.cancelUserControl}
          user={this.state.userControllerEntity}
          users={users}
          updateUser={updateUser}
          updateTeam={updateTeam}
          updateRegion={updateRegion}
          updateChannel={updateChannel}
          deleteUser={deleteUser}
          deleteTeam={deleteTeam}
          deleteRegion={deleteRegion}
          deleteChannel={deleteChannel}
          adminLoginAsUser={adminLoginAsUser}
          getTeams={getTeams}
        >
          <p>{Localize('form.no.cancel')}</p>
        </UserControl>
      </ContentBlock>
    );
  }
}

const mapStateToProps = state => {
  const currentUser = getCurrentUser(state);
  const users = getAllUsersGroupedByChannel(state);
  return {
    currentUser,
    users,
  };
};

const ConnectedUserListContainer = connect(
  mapStateToProps,
  Object.assign({}, userActions, teamActions, channelActions, regionActions, authActions)
)(UserListContainer);

export default ConnectedUserListContainer;



// WEBPACK FOOTER //
// ./src/modules/user/components/UserListContainer.js